import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { AgrSummaryCardModule } from '@next/core-lib/components';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
  AgrDialogModule,
} from '@next/core-lib/dialog';
import { AgrFormModule } from '@next/core-lib/form';
import { LocaleService } from '@next/core-lib/i18n';
import { AgrSharedModule } from '@next/core-lib/shared';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, skipWhile, takeUntil } from 'rxjs/operators';

import { FormMode, SiteState } from '../../../shared/models/models';
import {
  AnalysisType,
  ComponentLoadingOrder,
  Measurement,
  MeasurementAnalysis,
  MeasurementOutput,
  ProductType,
  RationType,
  SilageMethod,
  Species,
  Type,
} from '../../models/measurements.model';
import { calculateMeasurementSuccess } from '../../store/measurements.actions';
import { getMeasurement } from '../../store/measurements.selectors';
import { isValidTimeStringValidatorFn } from '../../utils/timeStringValidatorFn';
import { MeasurementAnalysisComponent } from '../measurement-analysis/measurement-analysis.component';
import { MeasurementLoadingOrderComponent } from '../measurement-loading-order/measurement-loading-order.component';

export interface MeasurementWizardDialogInputData extends AbstractFormDialogData {
  mode: FormMode;
}

export interface MeasurementWizardDialogResultData {
  measurement: Measurement;
}

@Component({
  selector: 'app-measurement-wizard',
  templateUrl: './measurement-wizard.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    AgrDialogModule,
    AgrSummaryCardModule,
    AgrFormModule,
    AgrSharedModule,
    MeasurementLoadingOrderComponent,
    MeasurementAnalysisComponent,
  ],
})
export class MeasurementWizardComponent
  extends AbstractFormDialog<MeasurementWizardDialogResultData>
  implements OnInit, OnDestroy
{
  data = inject<MeasurementWizardDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        MeasurementWizardComponent,
        AbstractDialogResultData<MeasurementWizardDialogResultData>
      >
    >(MatDialogRef);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly localeService = inject(LocaleService);

  readonly defaultTypes = [Type.Product, Type.Ration];
  readonly defaultProductTypes = [ProductType.Grass, ProductType.Maize, ProductType.Straw];
  readonly defaultRationTypes = [RationType.Tmr, RationType.Pmr];
  readonly defaultSpecies = [Species.MilkCow, Species.DryCow, Species.YoungStock];
  readonly defaultSilageMethods = [SilageMethod.Chopped, SilageMethod.LoaderWagon];

  readonly typeEnum = Type;
  readonly productTypeEnum = ProductType;
  readonly rationTypeEnum = RationType;
  readonly speciesEnum = Species;

  readonly minDsPercentageForOnlyFreshAnalysisView = 40;

  formGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    type: new FormControl(null),
    productType: new FormControl(null),
    silageMethod: new FormControl(null),
    rationType: new FormControl(null),
    species: new FormControl(null),
    hoursAfterFeeding: new FormControl<string>(null),
    dryMatter: new FormControl(null, [
      Validators.required,
      Validators.min(0.1),
      Validators.max(99.9),
    ]),
    grassSilage: new FormControl<number>(null),
    maizeSilage: new FormControl<number>(null),
    structureBasalRation: new FormControl<number>(null),
    freshGrassBasalRation: new FormControl<number>(null),
    ndf: new FormControl(null, [Validators.required, Validators.min(1), Validators.max(999)]),
    basalRation: new FormControl(null),
    separateFreshGrass: new FormControl(null),
    separateConcentrates: new FormControl(null),
  });

  types: Type[] = this.defaultTypes;
  rationTypesForType: RationType[] = this.defaultRationTypes;
  speciesForRationType: Species[] = this.defaultSpecies;

  measurement: Measurement;

  mixingTimeIsValid = true;
  freshAnalysisIsValid = true;
  dryAnalysisIsValid = true;

  showRestfeedDryAnalysis = false;

  measurement$ = this.store.select(getMeasurement).pipe(skipWhile((measurement) => !measurement));

  freshAnalysis$ = this.measurement$.pipe(
    map(
      (measurement) =>
        measurement.analyses?.find((analysis) => analysis.type === AnalysisType.Fresh) ||
        ({
          type: AnalysisType.Fresh,
        } as MeasurementAnalysis),
    ),
  );

  dryAnalysis$ = this.measurement$.pipe(
    map(
      (measurement) =>
        measurement.analyses?.find((analysis) => analysis.type === AnalysisType.Dry) ||
        ({
          type: AnalysisType.Dry,
        } as MeasurementAnalysis),
    ),
  );

  private readonly destroy$ = new Subject<void>();

  constructor() {
    super();
    this.data.title = this.localeService.translate(`measurements.wizard.title-${this.data.mode}`);
    this.data.subTitle = this.localeService.translate(
      `measurements.wizard.subtitle-${this.data.mode}`,
    );

    this.measurement$.pipe(takeUntil(this.destroy$)).subscribe((measurement) => {
      this.measurement = {
        ...measurement,
      };

      this.formGroup.setValue(
        {
          name: measurement.name || null,
          type: measurement.type || null,
          productType: measurement.productType || null,
          silageMethod: measurement.silageMethod || null,
          rationType: measurement.rationType || null,
          species: measurement.species || null,
          hoursAfterFeeding: measurement.hoursAfterFeeding || null,
          dryMatter: measurement.input?.dmPercentage || null,
          grassSilage:
            measurement.input?.dmGrassKilograms >= 0 ? measurement.input.dmGrassKilograms : null,
          maizeSilage:
            measurement.input?.dmMaizeKilograms >= 0 ? measurement.input.dmMaizeKilograms : null,
          structureBasalRation: measurement.input?.structureBasalRation || null,
          freshGrassBasalRation: measurement.input?.freshGrassBasalRation || null,
          ndf: measurement.input?.ndf || null,
          basalRation: measurement.input?.basalRationAmount || null,
          separateFreshGrass: measurement.input?.freshGrassAmount || null,
          separateConcentrates: measurement.input?.concentrateAmount || null,
        },
        { emitEvent: false },
      );
      this.updateFormControlsAfterTypeChange(measurement.type);
      this.updateFormControlsAfterRationTypeChange(measurement.rationType);
      this.updateFormControlsAfterProductTypeChange(measurement.productType);
      this.updateTypeFormControl();

      this.showRestfeedDryAnalysis = measurement.analyses?.some(
        (analysis) => analysis.type === AnalysisType.Dry,
      );
    });
  }

  ngOnInit(): void {
    this.formGroup.controls.type.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newType: Type) => {
        this.updateFormControlsAfterTypeChange(newType);
      });

    this.formGroup.controls.rationType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newRationType: RationType) => {
        this.updateFormControlsAfterRationTypeChange(newRationType);
      });

    this.formGroup.controls.productType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newProductType: ProductType) => {
        this.updateFormControlsAfterProductTypeChange(newProductType);
      });

    this.formGroup.controls.dryMatter.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((dryMatter: number) => {
        if (dryMatter >= this.minDsPercentageForOnlyFreshAnalysisView) {
          this.measurement = {
            ...this.measurement,
            analyses: this.measurement.analyses?.filter(
              (analysis) => analysis.type === AnalysisType.Fresh,
            ),
          };
          this.dryAnalysisIsValid = true;
        }
      });

    this.formGroup.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.measurement = this.retrieveMeasurement();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateFormControlsAfterTypeChange(newType: Type): void {
    this.rationTypesForType = newType === Type.Ration ? this.defaultRationTypes : [];
    this.showRestfeedDryAnalysis = this.measurement.analyses?.some(
      (analysis) => analysis.type === AnalysisType.Dry,
    );

    // 1
    if (newType === Type.Restfeed) {
      this.formGroup.controls.species.patchValue(
        this.measurement.species || this.speciesEnum.MilkCow,
        { emitEvent: false },
      );

      this.formGroup.controls.silageMethod.patchValue(null, { emitEvent: false });
      this.formGroup.controls.silageMethod.setValidators([]);
      this.formGroup.controls.silageMethod.updateValueAndValidity({ emitEvent: false });

      if (this.showRestfeedDryAnalysis !== true) {
        this.dryAnalysisIsValid = true;
      }
    }

    // 2
    if (newType === Type.Product) {
      this.formGroup.patchValue(
        {
          species: null,
          productType: this.measurement.productType || ProductType.Grass,
        },
        { emitEvent: false },
      );
    } else {
      this.formGroup.controls.productType.patchValue(null, { emitEvent: false });
    }

    // 3
    if (newType !== Type.Ration) {
      this.formGroup.patchValue(
        {
          rationType: null,
          grassSilage: null,
          maizeSilage: null,
          structureBasalRation: null,
          freshGrassBasalRation: null,
          basalRation: null,
          separateFreshGrass: null,
          separateConcentrates: null,
        },
        { emitEvent: false },
      );

      this.speciesForRationType = this.defaultSpecies;
      this.formGroup.controls.species.enable({ emitEvent: false });

      this.formGroup.controls.basalRation.setValidators([]);
      this.formGroup.controls.basalRation.updateValueAndValidity({ emitEvent: false });
      this.formGroup.controls.separateFreshGrass.setValidators([]);
      this.formGroup.controls.separateFreshGrass.updateValueAndValidity({ emitEvent: false });
      this.formGroup.controls.separateConcentrates.setValidators([]);
      this.formGroup.controls.separateConcentrates.updateValueAndValidity({ emitEvent: false });
      this.removeLoadingOrder();
      this.mixingTimeIsValid = true;
    } else {
      this.formGroup.controls.silageMethod.setValidators([Validators.required]);
      this.formGroup.controls.rationType.patchValue(this.measurement.rationType || RationType.Pmr, {
        emitEvent: false,
      });

      this.updateSpeciesFormControl(this.measurement.rationType || RationType.Pmr);

      this.formGroup.controls.basalRation.setValidators([
        Validators.required,
        Validators.min(0.1),
        Validators.max(99.9),
      ]);
      this.formGroup.controls.basalRation.updateValueAndValidity({ emitEvent: false });
      this.formGroup.controls.separateFreshGrass.setValidators([
        Validators.min(0.1),
        Validators.max(99.9),
      ]);
      this.formGroup.controls.separateFreshGrass.updateValueAndValidity({ emitEvent: false });

      const separateConcentratesValidators = [Validators.min(0.01), Validators.max(99.99)];
      if (this.measurement.rationType === RationType.Pmr) {
        separateConcentratesValidators.push(Validators.required);
      }
      this.formGroup.controls.separateConcentrates.setValidators(separateConcentratesValidators);
      this.formGroup.controls.separateConcentrates.updateValueAndValidity({ emitEvent: false });
    }
  }

  updateFormControlsAfterRationTypeChange(newRationType: RationType): void {
    if (this.measurement.type !== Type.Ration) {
      return;
    }

    // 1
    this.updateSpeciesFormControl(newRationType);

    // 2
    const separateConcentratesValidators = [Validators.min(0.01), Validators.max(99.99)];
    if (newRationType === RationType.Pmr) {
      separateConcentratesValidators.push(Validators.required);
    }
    this.formGroup.controls.separateConcentrates.setValidators(separateConcentratesValidators);
    this.formGroup.controls.separateConcentrates.updateValueAndValidity({ emitEvent: false });
  }

  updateFormControlsAfterProductTypeChange(newProductType: ProductType): void {
    if (this.measurement.type !== Type.Product) {
      return;
    }

    let validators: ValidatorFn[] = [];
    let value = null;
    if (newProductType === ProductType.Grass) {
      validators = [Validators.required];
      value = this.measurement.silageMethod;
    }

    this.formGroup.controls.silageMethod.patchValue(value, { emitEvent: false });
    this.formGroup.controls.silageMethod.setValidators(validators);
    this.formGroup.controls.silageMethod.updateValueAndValidity({ emitEvent: false });
  }

  updateTypeFormControl(): void {
    if (typeof this.measurement.relatedMeasurementId === 'number') {
      this.types = [Type.Restfeed];
      this.formGroup.controls.type.disable({ emitEvent: false });
      this.formGroup.controls.hoursAfterFeeding.setValidators([
        Validators.required,
        isValidTimeStringValidatorFn,
      ]);
    } else {
      this.types = this.defaultTypes;
      this.formGroup.controls.type.enable({ emitEvent: false });
    }
  }

  updateSpeciesFormControl(newRationType: RationType): void {
    if (newRationType === RationType.Pmr) {
      this.speciesForRationType = [Species.MilkCow];
      this.formGroup.controls.species.disable({ emitEvent: false });

      if (this.measurement.species !== Species.MilkCow) {
        this.formGroup.controls.species.patchValue(Species.MilkCow, { emitEvent: false });
      }
    } else {
      this.speciesForRationType = this.defaultSpecies;
      this.formGroup.controls.species.enable({ emitEvent: false });
    }
  }

  processAnalysisChange(newAnalysis: MeasurementAnalysis): void {
    let analyses;
    if (newAnalysis.type === AnalysisType.Fresh) {
      analyses = [
        newAnalysis,
        this.measurement.analyses?.find((analysis) => analysis.type === AnalysisType.Dry),
      ];
    } else {
      analyses = [
        this.measurement.analyses?.find((analysis) => analysis.type === AnalysisType.Fresh),
        newAnalysis,
      ];
    }
    analyses = analyses.filter((analysis) => !!analysis);

    this.measurement = {
      ...this.retrieveMeasurement(),
      analyses,
    };

    if (
      this.formGroup.invalid ||
      !this.freshAnalysisIsValid ||
      !this.dryAnalysisIsValid ||
      !this.mixingTimeIsValid
    ) {
      return;
    }

    this.store.dispatch(calculateMeasurementSuccess({ measurement: this.measurement }));
  }

  upsertMeasurement(): void {
    if (
      this.formGroup.invalid ||
      !this.freshAnalysisIsValid ||
      !this.dryAnalysisIsValid ||
      !this.mixingTimeIsValid
    ) {
      return;
    }

    this.confirm({
      measurement: this.sanitizeMeasurement(this.retrieveMeasurement()),
    });
  }

  processToggleChange(event: MatSlideToggleChange): void {
    if (event.checked === true) {
      return;
    }

    const analyses = this.measurement.analyses
      ?.slice()
      .filter((analysis) => analysis.type === AnalysisType.Fresh);
    this.measurement = {
      ...this.retrieveMeasurement(),
      analyses,
    };

    this.dryAnalysisIsValid = true;
    if (
      this.formGroup.invalid ||
      !this.freshAnalysisIsValid ||
      !this.dryAnalysisIsValid ||
      !this.mixingTimeIsValid
    ) {
      return;
    }

    this.store.dispatch(calculateMeasurementSuccess({ measurement: this.measurement }));
  }

  removeLoadingOrder(): void {
    const updatedMeasurement = this.retrieveMeasurement();
    updatedMeasurement.input = {
      ...updatedMeasurement.input,
      componentLoadingOrder: null,
      mixingTime: null,
    };

    this.measurement = updatedMeasurement;
  }

  processLoadingOrderChange(newLoadingOrder: ComponentLoadingOrder[]): void {
    const updatedMeasurement = this.retrieveMeasurement();
    updatedMeasurement.input = {
      ...updatedMeasurement.input,
      componentLoadingOrder: newLoadingOrder,
    };

    this.measurement = updatedMeasurement;
  }

  processMixingTimeChange(newMixingTime: number): void {
    const updatedMeasurement = this.retrieveMeasurement();
    updatedMeasurement.input = {
      ...updatedMeasurement.input,
      mixingTime: newMixingTime,
    };

    this.measurement = updatedMeasurement;
  }

  private retrieveMeasurement(): Measurement {
    return {
      id: this.measurement.id,
      name: this.formGroup.controls.name.value,
      type: this.formGroup.controls.type.value,
      productType: this.formGroup.controls.productType.value,
      rationType: this.formGroup.controls.rationType.value,
      species: this.formGroup.controls.species.value,
      silageMethod: this.formGroup.controls.silageMethod.value,
      relatedMeasurementId: this.measurement.relatedMeasurementId,
      hoursAfterFeeding: this.formGroup.controls.hoursAfterFeeding.value,
      input: {
        dmPercentage: this.formGroup.controls.dryMatter.value,
        ndf: this.formGroup.controls.ndf.value,
        dmGrassKilograms: this.formGroup.controls.grassSilage.value,
        dmMaizeKilograms: this.formGroup.controls.maizeSilage.value,
        structureBasalRation: this.formGroup.controls.structureBasalRation.value,
        freshGrassBasalRation: this.formGroup.controls.freshGrassBasalRation.value,
        basalRationAmount: this.formGroup.controls.basalRation.value,
        freshGrassAmount: this.formGroup.controls.separateFreshGrass.value,
        concentrateAmount: this.formGroup.controls.separateConcentrates.value,
        componentLoadingOrder: this.measurement.input?.componentLoadingOrder,
        mixingTime: this.measurement.input?.mixingTime,
      },
      analyses: this.measurement.analyses,
      output: this.measurement.output,
    };
  }

  private sanitizeMeasurement(measurement: Measurement): Measurement {
    return {
      ...measurement,
      analyses: measurement.analyses.slice().map((analysis) => ({
        type: analysis.type,
        sieveA: {
          grams: analysis.sieveA.grams,
        },
        sieveB: {
          grams: analysis.sieveB.grams,
        },
        sieveC: {
          grams: analysis.sieveC.grams,
        },
        containerD: {
          grams: analysis.containerD.grams,
        },
      })),
      output: {} as MeasurementOutput,
    };
  }
}
